/**
 * Utilitaires de gestion des permissions
 * Les permissions sont au format full_codename (ex: "core.view_animal", "core.add_user")
 */

/**
 * Vérifie si l'utilisateur a une permission spécifique
 * @param {Object} user - L'objet utilisateur du contexte
 * @param {string} requiredPermission - Permission requise (codename, ex: "core.view_animal")
 * @returns {boolean}
 */
export const hasPermission = (user, requiredPermission) => {
  if (!user || !requiredPermission) return false;
  
  // Si l'utilisateur est admin ou superuser, il a toutes les permissions
  if (user.is_admin || user.is_superuser) return true;
  
  // Si pas de permissions définies, retourner false
  if (!user.permissions || !Array.isArray(user.permissions)) return false;
  
  // Vérifier si la permission requise est dans la liste des permissions de l'utilisateur
  return user.permissions.includes(requiredPermission);
};

/**
 * Vérifie si l'utilisateur a au moins une des permissions (OR)
 * @param {Object} user - L'objet utilisateur du contexte
 * @param {string|string[]} permissions - Permission(s) requise(s)
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user || !permissions) return false;
  if (user.is_admin || user.is_superuser) return true;
  
  const permsArray = Array.isArray(permissions) ? permissions : [permissions];
  return permsArray.some(perm => hasPermission(user, perm));
};

/**
 * Vérifie si l'utilisateur a toutes les permissions (AND)
 * @param {Object} user - L'objet utilisateur du contexte
 * @param {string[]} permissions - Permissions requises
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user || !Array.isArray(permissions) || permissions.length === 0) return false;
  if (user.is_admin || user.is_superuser) return true;
  
  return permissions.every(perm => hasPermission(user, perm));
};

/**
 * Constantes des permissions disponibles dans l'application
 * Format: full_codename Django (app.action_model)
 * À adapter selon les permissions réelles du backend
 */
export const PERMISSIONS = {
  // Animaux
  VIEW_ANIMAUX: 'core.view_animal',
  ADD_ANIMAUX: 'core.add_animal',
  CHANGE_ANIMAUX: 'core.change_animal',
  DELETE_ANIMAUX: 'core.delete_animal',
  
  // Troupeaux
  VIEW_TROUPEAUX: 'core.view_troupeau',
  ADD_TROUPEAUX: 'core.add_troupeau',
  CHANGE_TROUPEAUX: 'core.change_troupeau',
  DELETE_TROUPEAUX: 'core.delete_troupeau',
  
  // Ranches
  VIEW_RANCHES: 'core.view_ranch',
  ADD_RANCHES: 'core.add_ranch',
  CHANGE_RANCHES: 'core.change_ranch',
  DELETE_RANCHES: 'core.delete_ranch',
  
  // Santé / Prophylaxie
  VIEW_SANTE: 'core.view_sante',
  ADD_SANTE: 'core.add_sante',
  CHANGE_SANTE: 'core.change_sante',
  DELETE_SANTE: 'core.delete_sante',
  
  // Utilisateurs
  VIEW_USER: 'core.view_user',
  ADD_USER: 'core.add_user',
  CHANGE_USER: 'core.change_user',
  DELETE_USER: 'core.delete_user',
  
  // Bergers
  VIEW_BERGER: 'core.view_berger',
  ADD_BERGER: 'core.add_berger',
  CHANGE_BERGER: 'core.change_berger',
  DELETE_BERGER: 'core.delete_berger',
  
  // Pharmacie / Produits pharmaceutiques
  VIEW_PHARMACIE: 'core.view_produitpharmaceutique',
  ADD_PHARMACIE: 'core.add_produitpharmaceutique',
  CHANGE_PHARMACIE: 'core.change_produitpharmaceutique',
  DELETE_PHARMACIE: 'core.delete_produitpharmaceutique',
  
  // Factures
  VIEW_FACTURE: 'core.view_facture',
  ADD_FACTURE: 'core.add_facture',
  CHANGE_FACTURE: 'core.change_facture',
  DELETE_FACTURE: 'core.delete_facture',
  
  // Événements
  VIEW_EVENEMENT: 'core.view_evenement',
  ADD_EVENEMENT: 'core.add_evenement',
  CHANGE_EVENEMENT: 'core.change_evenement',
  DELETE_EVENEMENT: 'core.delete_evenement',
  
  // Reproductions
  VIEW_REPRODUCTION: 'core.view_reproduction',
  ADD_REPRODUCTION: 'core.add_reproduction',
  CHANGE_REPRODUCTION: 'core.change_reproduction',
  DELETE_REPRODUCTION: 'core.delete_reproduction',
  
  // Rapports
  VIEW_RAPPORT: 'core.view_rapport',
  
  // Alertes
  VIEW_ALERTE: 'core.view_alerte',
  
  // Stocks pharmacie
  VIEW_STOCK_PHARMACIE: 'core.view_stockpharmacie',
  
  // Utilisations pharmacie
  VIEW_UTILISATION_PHARMACIE: 'core.view_utilisationpharmacie',
};

